import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { CardContent } from '@/design-system/atoms/Cards/CardContent';
import UIContentCarousel from '@/design-system/molecules/ContentCarousel/ContentCarousel';
import { generateStyleModifiers } from '@/utils/generateStyleModifiers';

import { formatEventDate } from './utils/formatEventDate';
import { stripUnitedStatesSuffix } from './utils/stripUnitedStatesSuffix';

const CATEGORIES = {
    virtual: { icon: 'monitor', label: 'Virtual' },
    inperson: { icon: 'map-pin', label: 'In-Person' },
    hybrid: { icon: 'user', label: 'Hybrid' },
};

const resolveCategory = (experience, location) => {
    const key = (experience || '').toLowerCase();
    const meta = CATEGORIES[key] || CATEGORIES.virtual;
    if (key === 'inperson') {
        return {
            icon: meta.icon,
            label: stripUnitedStatesSuffix(location) || meta.label,
        };
    }
    return { icon: meta.icon, label: meta.label };
};

const filterPastEvents = (rawItems) => {
    const now = new Date();
    return rawItems.filter((item) => {
        if (!item || typeof item.start !== 'string') return false;
        const eventDate = new Date(item.start);
        if (Number.isNaN(eventDate.getTime())) return false;
        return eventDate >= now;
    });
};

const UpcomingEvents = (props) => {
    const {
        appliedCssClassNames,
        title = 'Upcoming Events',
        cta,
        limit = 4,
        queryUrl,
        formats,
        promoteFeaturedEvents = false,
        isInEditor,
    } = props;

    const modifiers = generateStyleModifiers(appliedCssClassNames);
    const theme = modifiers.theme || 'treatments-and-services';

    const [items, setItems] = useState(null);

    const formatFilter = useMemo(
        () => (Array.isArray(formats) ? formats.map((f) => String(f).toLowerCase()) : []),
        [formats],
    );

    const filterByFormats = useCallback(
        (rawItems) => {
            if (formatFilter.length === 0) return rawItems;
            return rawItems.filter((item) =>
                formatFilter.includes(String(item.experience || '').toLowerCase()),
            );
        },
        [formatFilter],
    );

    useEffect(() => {
        if (!queryUrl) {
            setItems([]);
            return undefined;
        }
        let cancelled = false;
        const run = async () => {
            try {
                const response = await fetch(queryUrl);
                if (!response.ok) {
                    throw new Error(`Localist proxy returned HTTP ${response.status}`);
                }
                const payload = await response.json();
                if (cancelled) return;
                const list = Array.isArray(payload && payload.items) ? payload.items : [];
                const upcoming = filterByFormats(filterPastEvents(list));
                let sorted;
                if (promoteFeaturedEvents) {
                    const featured = upcoming
                        .filter((i) => i.featured)
                        .sort((a, b) => new Date(a.start) - new Date(b.start));
                    const nonFeatured = upcoming
                        .filter((i) => !i.featured)
                        .sort((a, b) => new Date(a.start) - new Date(b.start));
                    // Featured events take priority — if featured count >= limit, non-featured cards won't appear
                    sorted = [...featured, ...nonFeatured];
                } else {
                    sorted = upcoming.slice().sort((a, b) => new Date(a.start) - new Date(b.start));
                }
                setItems(sorted.slice(0, Number(limit)));
            } catch (error) {
                if (!cancelled) setItems([]);
                // eslint-disable-next-line no-console
                console.warn('UpcomingEvents fetch failed:', error);
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [queryUrl, limit, filterByFormats, promoteFeaturedEvents]);

    const carouselItems = useMemo(() => {
        if (!Array.isArray(items)) return [];
        return items.map((item, index) => {
            const category = resolveCategory(item.experience, item.location);
            return (
                <UIContentCarousel.Card key={`upcoming-event-${index}-${item.url}`} fullHeight>
                    <CardContent
                        eyebrow={formatEventDate(item.start)}
                        title={item.title}
                        body={item.description}
                        cta={{ href: item.url, csr: false }}
                        category={category}
                        titleLineClamp={3}
                        categoryLineClamp={2}
                        bodyLineClamp={3}
                        analyticsActionLabel={item.title}
                        analyticsComponentTitle={item.title}
                    />
                </UIContentCarousel.Card>
            );
        });
    }, [items]);

    if (!isInEditor && (items === null || items.length === 0)) {
        return null;
    }

    return <UIContentCarousel theme={theme} title={title} items={carouselItems} cta={cta} />;
};

export default UpcomingEvents;
